import { type ColumnDef } from '@tanstack/react-table';
import { PermissionGuard } from '@/features/auth';
import { Badge, Button, Table } from '@shared/components';
import type { AuthorisedContact } from '../../../types/authorisedContacts.types';
import { relationshipLabel } from '../../../utils/authorisedContactLabels';

interface Props {
    contacts: AuthorisedContact[];
    emptyMessage: string;
    onRevoke?: (contact: AuthorisedContact) => void;
    onReauthorise?: (contact: AuthorisedContact) => void;
}

export function AuthorisedContactsTable({ contacts, emptyMessage, onRevoke, onReauthorise }: Props) {
    const columns: ColumnDef<AuthorisedContact>[] = [
        {
            id: 'status',
            header: 'Status',
            cell: (info) =>
                info.row.original.status === 'active' ? (
                    <Badge size="xs" variant="success">Active</Badge>
                ) : (
                    <Badge size="xs" variant="error">Revoked</Badge>
                ),
        },
        {
            accessorKey: 'name',
            header: 'Name',
            cell: (info) => <span className="text-sm font-semibold">{info.row.original.name}</span>,
        },
        {
            accessorKey: 'relationship',
            header: 'Relationship',
            cell: (info) => <span className="text-sm">{relationshipLabel(info.row.original.relationship)}</span>,
        },
        {
            id: 'actions',
            header: '',
            cell: (info) => {
                const contact = info.row.original;

                if (contact.status === 'revoked') {
                    if (!onReauthorise) return null;

                    return (
                        <PermissionGuard permission={{ resource: 'agreement', action: 'authorised_contact.manage' }}>
                            <Button type="button" size="xs" variant="outline" onClick={() => onReauthorise(contact)}>
                                Re-authorise
                            </Button>
                        </PermissionGuard>
                    );
                }

                if (!onRevoke) return null;

                return (
                    <PermissionGuard permission={{ resource: 'agreement', action: 'authorised_contact.manage' }}>
                        <Button type="button" size="xs" variant="outline" onClick={() => onRevoke(contact)}>
                            Revoke
                        </Button>
                    </PermissionGuard>
                );
            },
            enableSorting: false,
        },
    ];

    return (
        <Table<AuthorisedContact>
            data={contacts}
            columns={columns}
            enableSorting={false}
            emptyMessage={emptyMessage}
            getRowId={(row) => row.id}
        />
    );
}
